import React from 'react';
import {
  TextField,
  InputAdornment,
  IconButton,
  Box,
  CircularProgress,
  Fade,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  onClear?: () => void;
  placeholder?: string;
  isLoading?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  variant?: 'outlined' | 'filled' | 'standard';
  size?: 'small' | 'medium';
  fullWidth?: boolean;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  value,
  onChange,
  onClear,
  placeholder = 'Pesquisar...',
  isLoading = false,
  disabled = false,
  autoFocus = false,
  variant = 'outlined',
  size = 'medium',
  fullWidth = true,
}) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {

    onChange(event.target.value);
  };

  const handleClear = () => {
    onChange('');
    onClear?.();
  };

  const showClearButton = value.length > 0 && !isLoading;

  return (
    <Box sx={{ position: 'relative', width: fullWidth ? '100%' : 'auto' }}>
      <TextField
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        variant={variant}
        size={size}
        fullWidth={fullWidth}
        disabled={disabled}
        autoFocus={autoFocus}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {isLoading ? (
                <CircularProgress size={20} />
              ) : (
                <SearchIcon color="action" />
              )}
            </InputAdornment>
          ),
          endAdornment: showClearButton && (
            <InputAdornment position="end">
              <Fade in={showClearButton}>
                <IconButton
                  onClick={handleClear}
                  size="small"
                  aria-label="Limpar pesquisa"
                  sx={{
                    padding: '4px',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Fade>
            </InputAdornment>
          ),
          sx: {
            borderRadius: 2,
            '& .MuiOutlinedInput-root': {
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
              },
              '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                  borderWidth: 2,
                },
              },
            },
          },
        }}
        sx={{
          '& .MuiInputBase-input': {
            fontSize: size === 'small' ? '0.875rem' : '1rem',
            '&::placeholder': {
              color: 'text.secondary',
              opacity: 0.7,
            },
          },
        }}
      />
    </Box>
  );
};